/**
 * Publisher CSS compatibility rewrites.
 *
 * Volpe paginates reflowable EPUBs the Readium way: the chapter body becomes a
 * horizontal CSS multi-column container and every column is one screen page.
 * Chromium only honours `break-before: column` inside that container, so the
 * forced *page* breaks publishers author (`page-break-before: always` on a
 * chapter title is what InDesign, Sigil, Vellum and most house stylesheets
 * emit) are silently ignored and a chapter runs on right after the previous
 * text. Apple Books and Kindle paginate natively and honour them, so the same
 * book looks broken only in Volpe. Appending the column equivalent next to each
 * forced page break makes the multicol layout honour the publisher's intent,
 * and is a no-op in scroll mode, where there is no fragmentation at all.
 *
 * The same pass unprefixes the `-epub-` properties (EPUB 3 CSS profile) that
 * browsers never implemented under that name but do support unprefixed.
 * `-epub-hyphens: auto` is the one that matters visually: it is how InDesign
 * asks for hyphenation, and justified prose without it shows the rivers Apple
 * Books avoids.
 *
 * Both rewrites keep the original declaration in place and only append, so a
 * reading system that honours the original keeps behaving as before. They
 * work on text, before the CSS parser drops the unknown `-epub-` properties,
 * and only ever see CSS: a stylesheet, a `<style>` block or a `style`
 * attribute value, never prose. Inside CSS, strings and comments are skipped,
 * a property name must start the declaration (so `--page-break-before` and
 * other custom properties do not count), and a value is a single keyword, so
 * a match can never run past its own declaration.
 *
 * No lookbehind: Fenice's iOS floor predates its WebKit support, and an
 * unsupported lookbehind is a SyntaxError at module load, not a silent miss.
 */

// Cheap check to skip the rewrites on text that cannot contain their targets.
const MAYBE_ADAPTABLE = /break-|-epub-/i;

// CSS strings and comments, matched first so nothing inside them is rewritten.
const SKIP = String.raw`\/\*[\s\S]*?\*\/|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'`;

// A declaration starts after `{`, `;`, whitespace or the start of the text.
const LEAD = String.raw`(?<lead>^|[^\w-])`;
// A declaration ends at `;`, `}` or the end of the text (an attribute value).
const END = String.raw`\s*(?<important>!important)?(?=\s*(?:[;}]|$))`;

// Forced page breaks, legacy (`page-break-*: always|left|right`) and modern
// (`break-*: page|always|left|right|recto|verso`) spellings in one pattern.
// `avoid*` and `auto` are excluded: they express a preference, not a break.
// The trailing lookahead skips a declaration already followed by its column
// twin, so running the pass twice over the same text changes nothing.
const FORCED_PAGE_BREAK = new RegExp(
    `${SKIP}|${LEAD}(?:page-)?break-(?<side>before|after)\\s*:\\s*(?<value>page|always|left|right|recto|verso)${END}(?!\\s*;\\s*break-\\k<side>\\s*:\\s*column)`,
    'gi'
);

// `-epub-` properties with a same-named unprefixed counterpart in browsers.
// All of them take a single keyword.
const EPUB_PREFIXED = new RegExp(
    `${SKIP}|${LEAD}-epub-(?<property>hyphens|text-align-last|line-break|word-break)\\s*:\\s*(?<value>[a-z-]+)${END}(?!\\s*;\\s*(?:-webkit-)?\\k<property>\\s*:)`,
    'gi'
);

/** Appends `break-{before,after}: column` after each forced page break. */
export function mapForcedPageBreaksToColumns(css) {
    return css.replace(FORCED_PAGE_BREAK, (match, ...rest) => {
        const { side, important } = rest[rest.length - 1]; // named groups
        if (side === undefined) return match; // a string or a comment
        return `${match}; break-${side}: column${important ? ' !important' : ''}`;
    });
}

/** Appends the unprefixed form (and `-webkit-hyphens`, which WebKit still needs) of each `-epub-` property. */
export function unprefixEpubProperties(css) {
    return css.replace(EPUB_PREFIXED, (match, ...rest) => {
        const { property, value, important } = rest[rest.length - 1];
        if (property === undefined) return match; // a string or a comment
        const flag = important ? ' !important' : '';
        const webkit = property === 'hyphens' ? `; -webkit-hyphens: ${value}${flag}` : '';
        return `${match}${webkit}; ${property}: ${value}${flag}`;
    });
}

/**
 * Applies every publisher-CSS compatibility rewrite to CSS text: a stylesheet,
 * a `<style>` block or a `style` attribute value. Returns the input untouched
 * when nothing applies.
 */
export function adaptPublisherCss(css) {
    if (!css || !MAYBE_ADAPTABLE.test(css)) return css;
    return unprefixEpubProperties(mapForcedPageBreaksToColumns(css));
}

/**
 * Applies the rewrites to the CSS embedded in an XHTML document: `<style>`
 * blocks and `style` attributes, in either quote style. Everything else in the
 * document, prose included, is left alone. Linked stylesheets go through
 * `adaptPublisherCss` when they are fetched, so they are not touched here.
 */
export function adaptPublisherCssInHtml(html) {
    if (!html || !MAYBE_ADAPTABLE.test(html)) return html;
    return html
        .replace(
            /(<style\b[^>]*>)([\s\S]*?)(<\/style\s*>)/gi,
            (_m, open, css, close) => `${open}${adaptPublisherCss(css)}${close}`
        )
        .replace(/\bstyle\s*=\s*(?:"([^"]*)"|'([^']*)')/gi, (m, doubleQuoted, singleQuoted) => {
            const css = doubleQuoted ?? singleQuoted;
            const adapted = adaptPublisherCss(css);
            if (adapted === css) return m;
            return doubleQuoted !== undefined ? `style="${adapted}"` : `style='${adapted}'`;
        });
}

/**
 * Applies the rewrites to the CSS already parsed into a chapter document:
 * `<style>` blocks and `style` attributes. The string pipeline covers these
 * before parsing for encrypted publications; legacy publications reach the
 * frame without passing through it, so their frames are adapted here.
 */
export function adaptPublisherCssInDocument(doc) {
    for (const style of doc.querySelectorAll('style')) {
        const adapted = adaptPublisherCss(style.textContent);
        if (adapted !== style.textContent) style.textContent = adapted;
    }
    for (const element of doc.querySelectorAll('[style*="break-"], [style*="-epub-"]')) {
        const value = element.getAttribute('style');
        const adapted = adaptPublisherCss(value);
        if (adapted !== value) element.setAttribute('style', adapted);
    }
}
